import type { Domain } from '@/lib/domain-context';
import { peekPostAuthIntent } from '@/lib/auth-intent';
import { readLastVisitedPage } from '@/lib/last-visited-page';
import { peekPendingInvite } from '@/lib/pending-invite';

/**
 * Clear the claims cache. Called on logout or session changes.
 */
export function clearClaimsCache(): void {
  // No-op: getClaims was removed as dead code. Kept for call-site compatibility.
}

/**
 * Get the post-login destination. THE ONLY PLACE THAT ANSWER IS COMPUTED.
 *
 * THE RULE
 * --------
 * Any surface that navigates after an auth-state check obtains its destination
 * from this function. A hardcoded `/chat` or `/dashboard` anywhere under
 * `src/components/auth/**` or `src/pages/auth/**` is a bug, and
 * `src/lib/__tests__/auth-surface-destination-guard.test.ts` fails the build on
 * one. Deliberate exceptions live in that test's allowlist, keyed by matched
 * text — today exactly one: `ImpersonateCallbackPage`, where admin
 * impersonation intentionally lands on `/chat`.
 *
 * Do not restate a count of call sites here. The previous version of this
 * docstring asserted a specific number of call sites across a specific list of
 * files, and was wrong on the count AND on the membership — one of the files it
 * credited was dead, and the live page that carried the ad funnel was not on
 * the list at all. A rule stays true as the codebase moves; a count starts
 * rotting the moment it is written (`.claude/rules/propagate-the-fix.md`:
 * re-derive the count, never quote it).
 *
 * THE 2026-09-10 INCIDENT THIS RULE EXISTS TO PREVENT
 * ---------------------------------------------------
 * The 2026 post-auth-intent fix was applied to `CombinedAuthPage.tsx` — a dead,
 * unrouted structural twin of the live `/auth` page — and missed `AuthPage.tsx`
 * itself, which computed `localStorage last-visited || '/chat'` on its own. It
 * then clobbered its own `AuthForm` child's correct destination, because React
 * flushes child effects before parent effects and the parent navigates last.
 * Every ad visitor who signed up for a NextGen trial landed on `/chat` with no
 * trial started, and nothing errored. The docstring naming the dead twin as a
 * covered call site is what made the coverage look real.
 *
 * PRECEDENCE: intent → pending invite → last-visited (SAME USER) → default
 * -------------------------------------------------------------------------
 * 1. `admin` short-circuits: an admin sign-in always lands on the admin
 *    dashboard. Cross-domain contamination is impossible anyway (localStorage
 *    is per-origin and admin/studicata/school are separate hosts), so this is
 *    belt-and-braces rather than the load-bearing guard.
 * 2. A live post-auth intent — what the visitor was TRYING to do when we sent
 *    them to /auth. It MUST outrank last-visited: someone who browsed while
 *    logged in, signed out, then signed up from an ad LP carries a stale
 *    last-visited value that would otherwise beat the product they just
 *    clicked for and paid us to reach.
 * 3. A pending study-group invite — the signed-out invitee's return target,
 *    written before they had a uid to key on. Below intent (a paid funnel the
 *    visitor arrived on outranks a link they clicked earlier) and above
 *    last-visited (accepting the invite is the thing they came to do). See
 *    `src/lib/pending-invite.ts` for why this is a separate store rather than
 *    an entry in the intent allowlist or a null-uid last-visited row.
 * 4. The last visited page (founder decision A, 2026-09-10) — this applies to
 *    ALL non-intent logins, not just the ones routed through the `/auth`
 *    wrapper. Ordinary returning users land where they left off rather than on
 *    a generic dashboard. Validated and cleared-if-invalid by
 *    `src/lib/last-visited-page.ts`; it is the only module that may touch that
 *    storage key.
 *
 *    THIS TIER IS KEYED BY USER and is skipped entirely when `uid` is absent.
 *    Storage is per-browser but "where I left off" is per-user, and for ~30
 *    minutes on 2026-09-10 those were conflated: a brand-new signup inherited
 *    the previous account's `/nextgen/skills/practice` from the same browser
 *    (board sYIAm69jLziJCcJifsMO). Pass the FIREBASE UID — the same identifier
 *    `useLastVisitedPage` writes. `AuthUser.id` is assigned `firebaseUser.uid`
 *    in `toAuthUser()`, so `useAuth().user.id` and `firebaseAuth.currentUser.uid`
 *    are interchangeable and either is correct. Passing a different identifier,
 *    or forgetting the argument, makes every comparison miss and silently
 *    reverts this whole tier to `/dashboard` with nothing erroring.
 * 5. `/dashboard`.
 *
 * THE READS HAVE DIFFERENT SIDE-EFFECT PROFILES. Do not assume this function is
 * free of side effects as a whole:
 *   - The INTENT and the PENDING INVITE are both PEEKED and never consumed
 *     here. These redirect sites run inside effects that can fire repeatedly
 *     (AuthForm carries a loop counter for that reason, and AuthPage reuses one
 *     computed value for its 500ms hard fallback), so repeated calls must
 *     return the same answer. Their destination pages consume them — see
 *     src/lib/auth-intent.ts and src/lib/pending-invite.ts.
 *   - The LAST-VISITED value is read AND VALIDATED, and an invalid, expired or
 *     FOREIGN-UID one is CLEARED as a side effect of reading it
 *     (`readLastVisitedPage()` in src/lib/last-visited-page.ts). That is
 *     deliberate: the value is a navigation target, so a rejected value must not
 *     sit in storage being re-rejected on every subsequent login, and a value
 *     belonging to a DIFFERENT account must not survive to be offered to the
 *     next one. The practical consequence is that calling this function twice
 *     can legitimately return a DIFFERENT answer the second time — the first
 *     call returns `/dashboard` and removes the bad value, and only a valid
 *     stored path owned by THIS uid survives to be returned again.
 *   - A stale pending invite is likewise cleared once past its TTL.
 *
 * The returned value can only ever be one of the literals in
 * `RETURN_TO_ALLOWLIST`, a validated same-origin path, or a hardcoded path
 * here — never an attacker-supplied string, and never off-origin. See
 * auth-intent.ts for why there is no `?returnTo=` param, and
 * last-visited-page.ts for why the protocol-relative forms (`//`, `/\`) and any
 * value carrying a control character or whitespace (`/<TAB>/evil.com` resolves
 * to `https://evil.com/`) are rejected on read.
 */
export function getPostLoginRedirect(
  domain: Domain,
  // REQUIRED, not optional — deliberately. An optional `uid?` makes the one
  // silent failure mode of this whole design (a caller that forgets the
  // argument, so the last-visited tier can never match and every login quietly
  // returns `/dashboard`) compile cleanly. Required, it is a build error.
  // Callers with genuinely no user pass an explicit `null` and say so.
  uid: string | null,
): string {
  if (domain === 'admin') return '/admin/dashboard';

  const intent = peekPostAuthIntent();
  if (intent) return intent.returnTo;

  const pendingInvite = peekPendingInvite();
  if (pendingInvite) return pendingInvite;

  const lastVisited = readLastVisitedPage(uid);
  if (lastVisited) return lastVisited;

  return '/dashboard';
}
